<template>
    <div>
        <rc-info-chip-dropdown
            icon="mdi-filter"
            :label="`${active_filters.length} Filter${active_filters.length !== 1 ? 's' : ''} Selected`"
            :dropdown_title="`Selected Filters (${active_filters.length})`"
            :is_empty="active_filters.length === 0"
            empty_icon="mdi-filter-off"
            empty_label="No Filters Selected"
            :max_width="700"
        >
            <template #dropdown-content>
                <v-simple-table
                    class="filters-table"
                    dense
                >
                    <tbody>
                        <tr
                            v-for="filter in active_filters"
                            :key="filter.key"
                        >
                            <td class="text-left filter-label">
                                {{ filter.label }}
                            </td>
                            <td class="text-right filter-value">
                                <rc-link
                                    v-if="filter.has_details_modal"
                                    text_transform="capitalize"
                                    :open_in_new_tab="false"
                                    @click="onFilterClick(filter.key)"
                                >
                                    {{ filter.value_description }}
                                </rc-link>
                                <template v-else>
                                    {{ filter.value_description }}
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </template>
        </rc-info-chip-dropdown>

        <v-dialog
            v-model="show_excluded_messages_dialog"
            max-width="600"
        >
            <v-card>
                <v-card-title class="text-h6">
                    Excluded Bulk Messages
                </v-card-title>

                <v-card-text class="pt-0">
                    <p class="text-body-2 mb-3">
                        An attempt was made to exclude recipients of the following bulk messages:
                    </p>
                    <ul class="pl-6">
                        <li
                            v-for="(message_label, index) in excluded_message_labels"
                            :key="index"
                            class="text-body-2 pt-1 px-0 mb-1"
                        >
                            {{ message_label }}
                        </li>
                    </ul>
                </v-card-text>

                <v-card-actions>
                    <v-spacer />

                    <v-btn
                        color="primary"
                        text
                        @click="show_excluded_messages_dialog = false"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: "SelectedFiltersSummary",

    props: {
        bulk_message: {
            type: Object,
            required: true
        },
    },

    data() {
        return {
            show_excluded_messages_dialog: false,
        }
    },

    computed: {
        filter_profile() {
            return this.bulk_message?.filter_profile || {};
        },

        visible_filters() {
            const excluded_filter_profile_keys = [
                'before_send_add_list_ids', // deprecated
                'technical_details', // not a filter
                'filter_twilio_carrier_deactivation_report_can_edit', // only for editing
                'default_number_reassignment_check_since_date_look_back_days', // used within filter_twilio_carrier_deactivation_report
            ];

            return Object.entries(this.filter_profile)
                .filter(([key, filter]) => {
                    if (excluded_filter_profile_keys.includes(key)) return false;
                    if (typeof filter !== 'object' || filter === null) return false;
                    return filter.visible === true;
                })
                .map(([key, filter]) => {
                    let value_description = filter.value_description;
                    let has_details_modal = false;

                    // special handling - display look-back days for Twilio carrier deactivation filter
                    if (key === 'filter_twilio_carrier_deactivation_report' && filter.value === true) {
                        const look_back_days =
                            this.bulk_message?.override_number_reassignment_check_since_date_look_back_days ??
                            this.filter_profile.default_number_reassignment_check_since_date_look_back_days;

                        if (look_back_days != null) {
                            value_description = `Yes (${look_back_days} day look-back)`;
                        }
                    }
                    // special handling - multiple excluded bulk messages into dialog
                    if (key === 'filter_recipients_in_other_bulk_messages' && Array.isArray(filter.value) && filter.value.length > 1) {
                        value_description = `${filter.value.length} messages`;
                        has_details_modal = true;
                    }

                    return {
                        key,
                        label: filter.label,
                        value_description,
                        backend_value_description: filter.value_description,
                        is_active: this.isFilterActive(filter),
                        has_details_modal,
                    };
                });
        },

        active_filters() {
            return this.visible_filters.filter(f => f.is_active);
        },

        excluded_message_labels() {
            const filter = this.active_filters.find(f => f.key === 'filter_recipients_in_other_bulk_messages');
            if (!filter?.backend_value_description) return [];

            const description = filter.backend_value_description;

            // 3+ messages: "A; B; or C" format
            if (description.includes('; ')) {
                return description
                    .replace(/; or /g, '; ')
                    .split('; ')
                    .filter(label => label.trim());
            }

            // 2 messages: "A or B" format
            if (description.includes(' or ')) {
                return description.split(' or ').filter(label => label.trim());
            }

            // 1 message: single label
            return [description];
        },
    },

    methods: {
        isFilterActive(filter) {
            const value = filter.value;
            if (value === null || value === false) return false;
            if (Array.isArray(value) && value.length === 0) return false;
            if (value === 'none') return false;
            return true;
        },

        onFilterClick(filter_key) {
            const filter_key_to_dialog_map = {
                filter_recipients_in_other_bulk_messages: 'show_excluded_messages_dialog',
            };

            const dialog_key = filter_key_to_dialog_map[filter_key];
            if (dialog_key) {
                this[dialog_key] = true;
            }
        },
    },
}
</script>

<style lang="scss" scoped>
// usage of !important is due to Vuetify table styles having high specificity
.filters-table {
    :deep(tbody tr:nth-of-type(even)) {
        background-color: transparent;
    }

    :deep(tbody tr:hover) {
        background: rgba(0, 0, 0, 0.02) !important;
    }

    :deep(td) {
        font-size: 0.8125rem !important;
        padding: 0.5rem 0.75rem !important;
        vertical-align: top;
    }

    .filter-label {
        max-width: 250px;
    }
    
    .filter-value {
        font-weight: 500;
        white-space: normal;
        word-break: break-word;
        max-width: 200px;
    }
}
</style>
